import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import TuneIcon from '@mui/icons-material/Tune';

import hubspot_logo from "../../assets/image/integrations/hubspot_logo.png"
import salesforce_logo from "../../assets/image/integrations/salesforce_logo.png"
import pipedrive_logo from "../../assets/image/integrations/pipedrive_logo.png"
import zoho_logo from "../../assets/image/integrations/zoho_logo.png"
import outreach_logo from "../../assets/image/integrations/outreach_logo.png"
import dynamics_logo from "../../assets/image/integrations/microsoft_dynamics_logo.png"
import close_logo from "../../assets/image/integrations/close_logo.webp"
import salesloft_logo from "../../assets/image/integrations/salesloft_logo.webp"
import apollo_logo from "../../assets/image/integrations/apollo_logo.png"
import mailchimp_logo from "../../assets/image/integrations/mailchimp_logo.webp"
import active_campaign_logo from "../../assets/image/integrations/active_campaign_logo.png"
import affinity_logo from "../../assets/image/integrations/affinity_logo.png"
import monday_logo from "../../assets/image/integrations/monday_logo.png"
import freshsales_logo from "../../assets/image/integrations/freshsales_logo.png"
import dialpad_logo from "../../assets/image/integrations/dialpad_logo.png"
import aircall_logo from "../../assets/image/integrations/aircall_logo.webp"
import customer_io_logo from "../../assets/image/integrations/customer_io_logo.webp"
import freshdesk_logo from "../../assets/image/integrations/freshdesk_logo.png"
import intercom_logo from "../../assets/image/integrations/intercom_logo.png"
import zendesk_logo from "../../assets/image/integrations/zendesk_logo.png"

import { useVesselLink } from "@vesselapi/react-vessel-link";
import { 
	getVesselConnections,
	getLinkToken,
	passPublicToken 
} from "../../services/Vessel";

import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";

import "./styles.css";

function IntegrationList() {
	const auth = useContext(AuthContext);
	const snackbar = useContext(SnackbarContext);

	const [refreshValue, setRefreshValue] = useState(0);
	const [hubspotStatus, setHubspotStatus] = useState({});
	const [salesforceStatus, setSalesforceStatus] = useState({});
	const [pipedriveStatus, setPipedriveStatus] = useState({});
	const [zohoStatus, setZohoStatus] = useState({});
	const [outreachStatus, setOutreachStatus] = useState({});
	
	const [activeTag, setActiveTag] = useState('')

	const navigate = useNavigate();

	const ruleToolTip = "You do not have any rules associated with this connection";

	const tags = [
		{
			id: 'crm',
			title: 'CRM'
		},
		{
			id: 'sales',
			title: 'Sales'
		},
		{
			id: 'marketing',
			title: 'Marketing'
		},
		{
			id: 'engagement',
			title: 'Engagement'
		}
	]

	const integrations = [
		{
			id: 'hubspot',
			title: 'HubSpot',
			logo: hubspot_logo,
			content: 'HubSpot offers a full platform of marketing, sales, customer service, and CRM software — plus the methodology, resources, and support — to help businesses grow better.',
			status: hubspotStatus,
			tags: ['crm', 'sales', 'marketing', 'engagement']
		},
		{
			id: 'salesforce',
			title: 'Salesforce',
			logo: salesforce_logo,
			content: 'The world’s #1 CRM platform that helps to connect with customers across sales, service, marketing, etc.',
			status: salesforceStatus,
			tags: ['crm', 'sales', 'marketing', 'engagement']
		},
		{
			id: 'pipedrive',
			title: 'Pipedrive',
			logo: pipedrive_logo,
			content: 'Sales pipeline and CRM software for deal makers with an emphasis on simplicity, visual design and ease-of-use.',
			status: pipedriveStatus,
			tags: ['crm', 'sales', 'engagement']
		},
		{
			id: 'zoho',
			title: 'Zoho',
			logo: zoho_logo,
			content: 'Zoho CRM is an online Sales CRM software that manages your sales, marketing and support in one CRM platform.',
			status: zohoStatus,
			tags: ['crm', 'sales', 'engagement']
		},
		{
			id: 'outreach',
			title: 'Outreach',
			logo: outreach_logo,
			content: 'Efficiently create more pipeline. Predictably close more of it. All in Outreach.',
			status: outreachStatus,
			tags: ['sales']
		},
		{
			id: 'dynamics',
			title: 'Dynamics 365',
			logo: dynamics_logo,
			content: 'Drive more efficiency, reduce costs, and create a hyperconnected business that links people, data, and processes across your organization—enabling every team to quickly adapt and innovate.',
			status: {},
			tags: ['crm', 'sales', 'engagement']
		},
		{
			id: 'close',
			title: 'Close',
			logo: close_logo,
			content: 'The CRM built for growth.',
			status: {},
			tags: ['crm', 'sales', 'engagement']
		},
		{
			id: 'salesloft',
			title: 'Salesloft',
			logo: salesloft_logo,
			content: 'Guide your go-to-market teams towards the right actions to yield the best outcomes: delighting customers, maximizing revenue, and increasing lifetime value.',
			status: {},
			tags: ['sales', 'engagement']
		},
		{
			id: 'apollo',
			title: 'Apollo',
			logo: apollo_logo,
			content: '',
			status: {},
			tags: ['sales']
		},
		{
			id: 'mailchimp',
			title: 'Mailchimp',
			logo: mailchimp_logo,
			content: '',
			status: {},
			tags: ['marketing', 'engagement']
		},
		{
			id: 'activeCampaign',
			title: 'Active Campaign',
			logo: active_campaign_logo,
			content: '',
			status: {},
			tags: ['marketing']
		},
		{
			id: 'affinity',
			title: 'Affinity',
			logo: affinity_logo,
			content: '',
			status: {},
			tags: ['crm', 'sales']
		},
		{
			id: 'monday',
			title: 'Monday',
			logo: monday_logo,
			content: '',
			status: {},
			tags: ['crm', 'sales', 'engagement']
		},
		{
			id: 'freshsales',
			title: 'Freshsales',
			logo: freshsales_logo,
			content: '',
			status: {},
			tags: ['sales']
		},
		{
			id: 'dialpad',
			title: 'Dialpad',
			logo: dialpad_logo,
			content: '',
			status: {},
			tags: ['sales', 'engagement']
		},
		{
			id: 'aircall',
			title: 'Aircall',
			logo: aircall_logo,
			content: '',
			status: {},
			tags: ['sales', 'engagement']
		},
		{
			id: 'customer',
			title: 'Customer.io',
			logo: customer_io_logo,
			content: '',
			status: {},
			tags: ['engagement']
		},
		{
			id: 'freshdesk',
			title: 'Freshdesk',
			logo: freshdesk_logo,
			content: '',
			status: {},
			tags: ['engagement']
		},
		{
			id: 'intercom',
			title: 'Intercom',
			logo: intercom_logo,
			content: '',
			status: {},
			tags: ['engagement']
		},
		{
			id: 'zendesk',
			title: 'Zendesk',
			logo: zendesk_logo,
			content: '',
			status: {},
			tags: ['engagement']
		},
	];
	
	useEffect(() => {
		getVesselConnections(auth.token, auth.userAccountID).then((response) => {
			if (response.ok) {
				response.json().then(resp => {
					if (resp.connections.length === 0) {
						setHubspotStatus({});
						setSalesforceStatus({});
						setPipedriveStatus({});
						setZohoStatus({});
						setOutreachStatus({});
					} else {
						resp.connections.forEach(connection => {
							if (connection.integration_id === 'hubspot') {setHubspotStatus(connection)}
							else if (connection.integration_id === 'salesforce') {setSalesforceStatus(connection)}
							else if (connection.integration_id === 'pipedrive') {setPipedriveStatus(connection)}
							else if (connection.integration_id === 'zoho') {setZohoStatus(connection)}
							else if (connection.integration_id === 'outreach') {setOutreachStatus(connection)}
						})
					}
				});
			} else {
				snackbar.createError("Error retrieving connections.");
			}
		}).catch((error) => {
			snackbar.createError("Error retrieving connections.");
		});
	}, [refreshValue, auth.token, auth.userAccountID]);

	const { open } = useVesselLink({
    onSuccess: (publicToken) => {
      passPublicToken(auth.token, auth.userAccountID, publicToken).then(response => {
				if (response.ok) {
					setRefreshValue(refreshValue+1);
					snackbar.createSuccess('Connection installed.')
				}
			});
    },
    onClose: () => console.log('closed'),
    onLoad: () => console.log('loaded'),
  });

	return (
		<Box
			sx={{margin: "24px 0"}}
		>
			<Box sx={{display:"flex", justifyContent:"center", margin: "24px 0"}}>
				{tags.map((tag) => (
					<Button 
						key={tag.title}
						className="integrationBtn"
						variant={ activeTag === tag.id
							? "contained"
							: "outlined" 
						}
						color="primary"	
						sx={{mx:1}}
						onClick={() => {
							if (activeTag === tag.id) {setActiveTag("")}
							else {setActiveTag(tag.id)}
						}}
					>
						{tag.title}
					</Button>
				))}
			</Box>
			<Grid 
				container 
				spacing={3}
				columns={5}
				direction="row"
				alignItems="center"
			>
				{integrations.map((i) => (
					(activeTag === "" || i.tags.includes(activeTag)) &&
						<Grid item xs={1} key={i.id}>
							<Card 
								className="integrationCard"
							>
								<CardContent sx={{py:0, textAlign: "center"}}>
									<Typography variant="h6" fontWeight={"bold"}>{i.title}</Typography>
								</CardContent>
								<CardContent sx={{display:"flex", pb:2}}>
									<img src={i.logo} alt={i.title + ' Logo'} className="logoImage"/>
								</CardContent>
								<CardActions sx={{display:"flex", justifyContent:"center"}}>
									{Object.keys(i.status).length === 0 ? (
										<Button
											className="integrationBtn"
											variant="outlined"
											color="primary"
											startIcon={<PowerSettingsNewIcon />}
											sx={{ width: "80%", fontWeight: "bold" }}
											onClick={async () =>
												open({
													integrationId: i.id,
													linkToken: await getLinkToken(auth.token)
												})
											}
										>
											Turn On  
										</Button>
									) : (
										<Button
											className="integrationBtn"
											variant="contained"
											color="primary"
											sx={{ width: "80%", fontWeight: "bold" }}
											onClick={() => {navigate(`/account/${auth.userAccount.id}/integrations/${i.id}`)}}
										>
											{i.status.rules?.length === 0 ? (
												<>
													<Tooltip
														title={ruleToolTip}
														arrow
														placement="bottom"
														sx={{px:1}}
													>
														<NewReleasesIcon  color="alert" />
													</Tooltip>
													<TuneIcon sx={{pr:1}}/>
												</>
											): (
												<TuneIcon sx={{pr:1}}/>
											)}
											Manage
										</Button>
									)}
								</CardActions>
							</Card>
						</Grid>
				))}
			</Grid>
		</Box>
	)
}

export default IntegrationList;